import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, InputNumber, Select, Switch } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

import { apiFetch } from '@/api/client';
import { itemMediaKey, itemsKey, useItemMedia, type Item, type ItemCategory } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { uploadItemMedia } from '@/api/upload';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { useOpenAtTop } from '@/components/useOpenAtTop';
import { IconAction } from '@/components/IconAction';
import { Button, Card, Notice } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import type { Paged } from '@core/api/masters';
import { itemsPath } from '@core/api/queryPaths';
import { blankToNull } from '@core/format/text';
import { blockedReason } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { shortCodeProblem, suggestShortCode } from '@core/units/garmentCode';

import { MediaGallery, ProgressRow } from '../MediaGallery';
import {
  acceptedTypes,
  categoryChoices,
  designRequest,
  isImage,
  maxFilesPerDesign,
  salePriceField,
  saleRateField,
  stageFiles,
  type StagedFile,
} from './designForm';

interface DesignFormValues {
  code: string;
  name: string;
  categoryId: number;
  /** Create only — see the note on the switch below. */
  shared?: boolean;
  description?: string | null;
  brand?: string | null;
  fabric?: string | null;
  primaryImageUrl?: string | null;
  turnaroundBufferDays?: number | null;
  hsnCode?: string | null;
  sacCode?: string | null;
  taxRatePct?: number | null;
  saleTaxRatePct?: number | null;
  salePrice?: number | null;
  replacementValue?: number | null;
  isActive: boolean;
}

/** What one Save did: the design as written, and the files that did not go up. */
interface Saved {
  item: Item;
  failed: StagedFile[];
}

/**
 * A design, new or existing, on a page of its own (spec Section 6).
 *
 * <p>
 * <strong>A page, not a drawer</strong> — the rule settled on 2026-09-08 — laid out as the
 * template's add-product page: what the design is on the left, its photographs and status on the
 * right. It replaces the drawer the catalogue used to open.
 * </p>
 *
 * <p>
 * <strong>Photographs are chosen before saving and sent by Save</strong>, so a design is never saved
 * and forgotten without one — the reason the booking redesign gave for this page. A new design has no
 * id until its insert has run, so Save writes the design first and then sends each staged file in
 * order; the first photograph becomes the main one. A file that fails stays staged with its reason,
 * and the page stays open on the design it saved, so the next Save edits it and sends only what is
 * still missing. Photographs already saved are promoted and removed at once, as in the gallery
 * drawer — those are writes on files that exist, not part of this form.
 * </p>
 *
 * <p>
 * <strong>The old rental rate is gone from here.</strong> Nothing charges it — a design's price is
 * its rate card — and a field nothing reads is the defect this codebase keeps recording. Whatever is
 * stored is sent back untouched until the migration that drops the column.
 * </p>
 */
export function DesignPage({
  session,
  item,
  categories,
  onClose,
}: {
  session: Session;
  /** The design being edited, or undefined for a new one. */
  item: Item | undefined;
  categories: ItemCategory[];
  onClose: () => void;
}) {
  const [form] = Form.useForm<DesignFormValues>();
  const queryClient = useQueryClient();

  // Becomes the created design if Save wrote it but not all of its photographs, so the next Save
  // edits rather than creating a second one.
  const [existing, setExisting] = useState(item);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [refused, setRefused] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ name: string; fraction: number } | null>(null);

  const isEdit = existing !== undefined;
  const fields = useAutoFocusFirstField<HTMLDivElement>();
  useOpenAtTop();

  const saved = useItemMedia(existing?.id);
  const savedRows = saved.data ?? [];

  // Watched rather than read once: a shared design may only sit in a shared category, so ticking
  // "shared" narrows the category list at once rather than failing on save.
  const shared = Form.useWatch('shared', form) ?? false;

  // The short code is suggested from the name until somebody types their own (BJB for Black
  // Jodhpuri Bandhgala), and checked against the codes already in use that start the same way. The
  // server is still the guard; this only keeps the first suggestion from being one it refuses.
  const name = Form.useWatch('name', form) ?? '';
  const code = Form.useWatch('code', form) ?? '';
  const firstGuess = isEdit ? null : suggestShortCode(name);
  const similar = useQuery({
    queryKey: [...itemsKey, 'code-check', firstGuess],
    queryFn: () => apiFetch<Paged<Item>>(itemsPath({ search: firstGuess ?? '', pageSize: 100 })),
    enabled: firstGuess !== null,
    staleTime: 30_000,
  });
  const suggestion = isEdit
    ? null
    : suggestShortCode(name, new Set(similar.data?.items.map((i) => i.code.toUpperCase())));

  useEffect(() => {
    if (!isEdit && !form.isFieldTouched('code')) {
      form.setFieldValue('code', suggestion ?? '');
    }
  }, [form, isEdit, suggestion]);

  const codeProblem = isEdit ? null : shortCodeProblem(code);

  const save = useApiMutation<Saved, DesignFormValues>({
    mutationFn: async (raw) => {
      const { saleTaxRatePct, salePrice, ...values } = blankToNull(raw);
      const body = designRequest(
        {
          ...values,
          ...saleRateField(saleTaxRatePct ?? null, existing !== undefined),
          ...salePriceField(salePrice ?? null, existing !== undefined),
        },
        existing,
      );

      const written = existing
        ? await apiFetch<Item>(`/api/item/${existing.id}`, { method: 'PUT', body })
        : await apiFetch<Item>('/api/item', { method: 'POST', body });

      // One at a time, in the order chosen, so the first photograph is the one that becomes main
      // and a shop's upstream finishes one file before starting the next.
      const failed: StagedFile[] = [];

      for (const entry of staged) {
        setProgress({ name: entry.file.name, fraction: 0 });

        try {
          await uploadItemMedia(written.id, entry.file, (fraction) =>
            setProgress({ name: entry.file.name, fraction }),
          );
        } catch (error) {
          failed.push({
            ...entry,
            error: error instanceof Error ? error.message : 'could not be uploaded.',
          });
        }
      }

      setProgress(null);

      return { item: written, failed };
    },
    success: ({ failed }) =>
      failed.length > 0
        ? `Design saved. ${failed.length} file${failed.length === 1 ? '' : 's'} did not go up — see the photographs.`
        : isEdit
          ? 'Design updated.'
          : 'Design created.',
    invalidates: [itemsKey, itemMediaKey],
    onSuccess: ({ item: written, failed }) => {
      if (failed.length === 0) {
        onClose();

        return;
      }

      setExisting(written);
      setStaged(failed);
    },
  });

  // Photographs are written through the design's edit grant, so a role that may create designs but
  // not edit them is told here rather than after its design has been saved without them.
  const photoBlocked = blockedReason(session, 'Item:Edit');

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to items"
            className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
          >
            <i className="icon icon-arrow-left" />
          </button>
          <div>
            <h1 className="page-title">{existing ? `Edit ${existing.name}` : 'New design'}</h1>
            <p className="page-subtitle">
              A design the shop rents. The physical garments hang off it as units.
            </p>
          </div>
        </div>
      </div>

      {/*
        No <form>: the photographs card carries its own controls, and a nested form is invalid HTML
        whose buttons submit the outer one. Enter navigation comes from `enterKeyScope` on the grid,
        and the last field hands focus to the save button.
      */}
      <Form<DesignFormValues>
        form={form}
        component={false}
        layout="vertical"
        requiredMark={false}
        initialValues={existing ?? { code: '', name: '', shared: false, isActive: true }}
        onFinish={(values) => {
          // What was turned away at choosing is old news once the person has moved on to saving.
          setRefused([]);
          void save.mutateAsync(values).catch(() => undefined);
        }}
      >
        <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
          {/* ----------------------------------------------------- left column */}
          <div className="col-span-12 flex flex-col gap-4 lg:col-span-8">
            <Card>
              <SectionTitle>Basic information</SectionTitle>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'A name is required' }]}
                >
                  <Input placeholder="Black Jodhpuri Bandhgala" />
                </Form.Item>

                {/* After the name, because it is suggested from it. */}
                <Form.Item
                  name="code"
                  label="Short code"
                  rules={[{ required: true, message: 'A short code is required' }]}
                  extra={
                    isEdit
                      ? 'Fixed at creation — every garment code, tag and rate card refers to it.'
                      : (codeProblem ??
                        `Starts every garment code: ${(code || 'BJB').toUpperCase()}-36-01. Suggested from the name; change it if you like. Fixed once created.`)
                  }
                  // Set only for the advisory; set always, it would hide the required-field error.
                  {...(codeProblem && code ? { validateStatus: 'warning' as const } : {})}
                >
                  <Input disabled={isEdit} className="font-mono-nums uppercase" maxLength={20} />
                </Form.Item>
              </div>

              {/*
                Create only, as on the categories page. Narrowing a shared design hides it at every
                branch where its units and bookings already point at it, so the server fixes scope at
                creation and the page does not offer to change it.
              */}
              {!isEdit && (
                <Form.Item
                  name="shared"
                  label="Shared across all branches"
                  valuePropName="checked"
                  extra="On: every branch stocks this design. Off: only the branch you are working at. Sharing needs a company-wide role, and a shared design can only sit in a shared category."
                >
                  <Switch size="small" />
                </Form.Item>
              )}

              <Form.Item
                name="categoryId"
                label="Category"
                rules={[{ required: true, message: 'Pick a category' }]}
                extra="Turnaround buffer, damage rates and GST codes are inherited from it unless set below."
              >
                <Select
                  options={categoryChoices(categories, shared, isEdit)}
                  placeholder="Pick a category"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <Form.Item name="brand" label="Brand">
                  <Input placeholder="Raymond, Manyavar" />
                </Form.Item>

                <Form.Item name="fabric" label="Fabric" extra="Feeds the cleaning instructions.">
                  <Input placeholder="Wool blend, raw silk" />
                </Form.Item>
              </div>

              <Form.Item name="description" label="Description" className="mb-0">
                <Input.TextArea rows={2} placeholder="What this design is." />
              </Form.Item>
            </Card>

            <Card>
              <SectionTitle>Value, turnaround and tax</SectionTitle>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <Form.Item
                  name="replacementValue"
                  label="Replacement value"
                  extra="Charged if the garment never comes back. The rental price is the design's rate card."
                >
                  <InputNumber min={0} precision={2} className="w-full" />
                </Form.Item>

                <Form.Item
                  name="turnaroundBufferDays"
                  label="Turnaround buffer"
                  extra="Cleaning days before a returned garment is bookable again. Leave empty to inherit from the category."
                >
                  <InputNumber min={0} precision={0} addonAfter="days" className="w-full" />
                </Form.Item>
              </div>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-4">
                <Form.Item
                  name="hsnCode"
                  label="HSN"
                  extra="Goods code, for a sale."
                  className="mb-0"
                >
                  <Input className="font-mono-nums" />
                </Form.Item>

                <Form.Item
                  name="sacCode"
                  label="SAC"
                  extra="Service code, for a rental."
                  className="mb-0"
                >
                  <Input className="font-mono-nums" />
                </Form.Item>

                <Form.Item name="taxRatePct" label="Rental tax" extra="On a hire." className="mb-0">
                  <InputNumber min={0} max={100} precision={2} addonAfter="%" className="w-full" />
                </Form.Item>

                <Form.Item
                  name="saleTaxRatePct"
                  label="Sale tax"
                  extra={
                    existing?.saleTaxRatePct == null && existing?.effectiveSaleTaxRatePct != null
                      ? `Empty takes ${existing.effectiveSaleTaxRatePct}% from its category.`
                      : 'When sold. Empty takes the category’s.'
                  }
                  className="mb-0"
                >
                  <InputNumber min={0} max={100} precision={2} addonAfter="%" className="w-full" />
                </Form.Item>

                <Form.Item
                  name="salePrice"
                  label="Sale price"
                  extra="GST included. Filled in when one is sold; the counter can change it."
                  className="mb-0"
                >
                  <InputNumber min={0} precision={2} addonBefore="₹" className="w-full" />
                </Form.Item>
              </div>
            </Card>
          </div>

          {/* ---------------------------------------------------- right column */}
          <div className="col-span-12 flex flex-col gap-4 lg:col-span-4">
            <Card>
              <SectionTitle>Photographs</SectionTitle>

              <StagingArea
                staged={staged}
                savedCount={savedRows.length}
                blocked={photoBlocked}
                busy={save.isPending}
                onAdd={(files) => {
                  const result = stageFiles(staged, files, savedRows.length, () =>
                    crypto.randomUUID(),
                  );

                  setStaged((current) => [...current, ...result.added]);
                  setRefused(result.refused);
                }}
                onRemove={(id) => setStaged((current) => current.filter((s) => s.id !== id))}
              />

              {progress && (
                <div className="mt-3">
                  <ProgressRow name={progress.name} fraction={progress.fraction} />
                </div>
              )}

              {refused.length > 0 && (
                <div className="mt-3">
                  <Notice tone="warning" title="Not added">
                    <ul className="mt-1 flex flex-col gap-0.5 ps-4">
                      {refused.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </Notice>
                </div>
              )}

              {existing && (
                <div className="mt-4 border-t border-border-color pt-4">
                  <h3 className="mb-2 text-[13px] font-semibold text-gray-900">Saved</h3>

                  {saved.isError && <ApiErrorAlert error={saved.error} />}

                  {savedRows.length === 0 ? (
                    <p className="text-default mb-0 text-[length:var(--scale-field-hint)]">
                      Nothing saved yet.
                    </p>
                  ) : (
                    <MediaGallery
                      session={session}
                      scope={{ level: 'item', itemId: existing.id, name: existing.name }}
                      rows={savedRows}
                    />
                  )}
                </div>
              )}

              <Form.Item
                name="primaryImageUrl"
                label="Or a link to a photo hosted elsewhere"
                extra="Shown only while the design has no uploaded photograph."
                className="mt-4 mb-0"
              >
                <Input placeholder="https://…" />
              </Form.Item>
            </Card>

            {isEdit && (
              <Card>
                <SectionTitle>Status</SectionTitle>

                <Form.Item
                  name="isActive"
                  label="Active"
                  valuePropName="checked"
                  extra="Switch off to stop offering this design without deleting it."
                  className="mb-0"
                >
                  <Switch size="small" />
                </Form.Item>
              </Card>
            )}

            <Card>
              <div className="flex flex-col gap-2">
                <Button
                  variant="primary"
                  icon="icon icon-check"
                  data-enter-key-commit
                  shortcut="save"
                  loading={save.isPending}
                  disabled={save.isPending}
                  onClick={() => form.submit()}
                >
                  {save.isPending
                    ? progress
                      ? 'Uploading…'
                      : 'Saving…'
                    : saveLabel(isEdit, staged.length)}
                </Button>

                <Button
                  variant="secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    // A design created by a Save whose uploads failed is real, so the list must show
                    // it even though nothing more is written here.
                    if (existing && !item) {
                      void queryClient.invalidateQueries({ queryKey: itemsKey });
                    }

                    onClose();
                  }}
                >
                  {staged.some((s) => s.error) ? 'Finish without them' : 'Cancel'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </Form>
    </>
  );
}

function saveLabel(isEdit: boolean, stagedCount: number): string {
  const verb = isEdit ? 'Save changes' : 'Create design';

  return stagedCount === 0
    ? verb
    : `${verb} and add ${stagedCount} file${stagedCount === 1 ? '' : 's'}`;
}

/**
 * The files chosen and not yet sent: a grid of previews, each with its reason if it failed last
 * time, and the template's dashed card to add more.
 */
function StagingArea({
  staged,
  savedCount,
  blocked,
  busy,
  onAdd,
  onRemove,
}: {
  staged: StagedFile[];
  savedCount: number;
  blocked: string | undefined;
  busy: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const full = savedCount + staged.length >= maxFilesPerDesign;

  // The server makes the first photograph a gallery gets its main picture — never a clip — so the
  // badge goes where that will land, and only when the design has nothing saved yet.
  const firstPhoto = savedCount === 0 ? staged.find((s) => isImage(s.file))?.id : undefined;
  const disabled = blocked !== undefined || busy || full;

  // Previews of the chosen files, released when the set changes or the page closes — an object URL
  // holds its file in memory until it is revoked.
  const previews = useMemo(
    () =>
      new Map(
        staged
          .filter((s) => isImage(s.file))
          .map((s) => [s.id, URL.createObjectURL(s.file)] as const),
      ),
    [staged],
  );

  useEffect(
    () => () => {
      for (const url of previews.values()) {
        URL.revokeObjectURL(url);
      }
    },
    [previews],
  );

  return (
    <div className="flex flex-col gap-3">
      {staged.length > 0 && (
        <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-2">
          {staged.map((entry) => (
            <figure key={entry.id} className="m-0 flex flex-col gap-1">
              <div className="border-border-color relative aspect-square overflow-hidden rounded border bg-light">
                {previews.has(entry.id) ? (
                  <img
                    src={previews.get(entry.id)}
                    alt={entry.file.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-default flex h-full w-full items-center justify-center">
                    <i className="icon icon-play text-[22px]" />
                  </span>
                )}

                <span className="absolute start-1 top-1">
                  {entry.error ? (
                    <StatusBadge tone="warning">Not sent</StatusBadge>
                  ) : entry.id === firstPhoto ? (
                    <StatusBadge tone="brand">Main</StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">To add</StatusBadge>
                  )}
                </span>
              </div>

              <figcaption className="flex items-center gap-1">
                <span className="text-default truncate text-[11px]" title={entry.file.name}>
                  {entry.file.name}
                </span>
                <span className="ms-auto">
                  <IconAction
                    icon="icon icon-x"
                    label={`Do not add ${entry.file.name}`}
                    disabled={busy}
                    onClick={() => onRemove(entry.id)}
                  />
                </span>
              </figcaption>

              {entry.error && (
                <span className="text-warning text-[11px] leading-snug">{entry.error}</span>
              )}
            </figure>
          ))}
        </div>
      )}

      <input
        ref={input}
        type="file"
        accept={acceptedTypes}
        multiple
        className="hidden"
        onChange={(event) => {
          onAdd(Array.from(event.target.files ?? []));
          // Cleared so choosing the same file again after removing it still fires a change.
          event.target.value = '';
        }}
      />

      <button
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();

          if (!disabled) {
            onAdd(Array.from(event.dataTransfer.files));
          }
        }}
        className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border-color p-6 text-center hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i className="ph-duotone ph-cloud-arrow-up text-default text-3xl" />
        <span className="text-default text-[length:var(--scale-font-size)]">
          {full ? 'This design is full' : 'Choose or drop photographs'}
        </span>
      </button>

      <p className="text-default mb-0 text-[length:var(--scale-field-hint)]">
        {blocked ??
          `Sent when you save, in this order — the first becomes the main picture. Up to ${maxFilesPerDesign} per design; photographs to 10 MB, clips to 200 MB.`}
      </p>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-3 text-sm font-semibold text-gray-900">{children}</h2>;
}
